import { sql } from 'drizzle-orm'
import { WriteTransaction } from 'replicache'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { BlockBatchMutationParams } from '../block/mutators/batch'
import {
  BlockRelationship,
  RootBlock,
  branchBaseOps,
  isBRInRoot,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { Entry } from '@basehub/data/db/queries'
import { FastForwardInput, fastForward } from '../commit/fast-forward'
import pMap from 'p-map'
import { mergeBlocks } from '../commit/merge-helpers'
import { getIdempotentId } from '@basehub/utils/id/index'

export type ApplySuggestionInput = {
  now: string
  branchId: string
  rootBlockId: string
  baseRootBlockId: string
  baseBranchId: string
}

export const applySuggestion = async (
  tx: WriteTransaction,
  { now, branchId, rootBlockId, baseRootBlockId }: ApplySuggestionInput
) => {
  ensureBaseHubTransaction(tx)

  const undo: BlockBatchMutationParams['mutations'] = []

  const baseTreeManagerPromise = tx.getTreeManager(baseRootBlockId)

  const [rootBlock] = await Promise.all([blockBaseOps.get(tx, rootBlockId)])
  invariant(rootBlock, `rootBlock ${rootBlockId} not found`)
  invariant(
    isRootBlock(rootBlock),
    `rootBlock ${rootBlockId} not root: ${rootBlock.type}`
  )

  const brs: BlockRelationship[] = []
  if (tx.serverOnly) {
    const result = await tx.serverOnly.executor.execute<
      Entry<BlockRelationship>
    >(
      sql`select * from entry where key like 'br/%' and spaceid = ${tx.serverOnly.spaceID} and value->>'branchId' = ${branchId} and value->>'isInlineSuggestion' = 'true'`
    )
    result.forEach((brEntry) => {
      const br = brEntry.value
      tx.blockRelationshipCache.set(br.id, br)
      if (!isBRInRoot(br, rootBlock)) return
      brs.push(br)
    })
  } else {
    for await (const br of tx.scan<BlockRelationship>({
      indexName: 'brsByBranchId',
      prefix: branchId,
    })) {
      if (!isBRInRoot(br, rootBlock)) continue
      if (!br.isInlineSuggestion) continue
      brs.push(br)
    }
  }

  const ffParams: FastForwardInput = {
    rootBlockId: baseRootBlockId,
    deleted: [],
    upserted: [],
    undo,
    now,
  }

  const baseTreeManager = await baseTreeManagerPromise
  const baseRootBlock = baseTreeManager.root.value as RootBlock
  const baseBRs = await tx.getBrsByRootId(baseRootBlock)

  await pMap(brs, async (br) => {
    const blockInBase = baseTreeManager.getNode(br.sourceBlockId)
    const brInBase = baseBRs.get(br.sourceBlockId)
    if (br.deletedAt && blockInBase) {
      ffParams.deleted.push(blockInBase.value.id)
      return
    }
    const block = await blockBaseOps.get(tx, br.blockId)
    invariant(block, `block ${br.blockId} not found`)

    const mergeResult = mergeBlocks({
      idSeed:
        // suggestions can be applied multiple times
        // this ensures the op is idempotent
        getIdempotentId({
          idSeed: `apply-suggestion-${branchId}-${br.sourceBlockId}`,
        }),
      now,
      sourceRootBlock: baseRootBlock,
      source:
        blockInBase && brInBase
          ? { block: blockInBase.value, br: brInBase }
          : null,
      head: { block, br },
      commonAncestor: null,
      resolveConflict: () => {
        // always pro-suggestion
        return 'head'
      },
    })

    if (mergeResult.value) {
      ffParams.upserted.push(mergeResult.value)
    }
  })

  await fastForward(tx, ffParams)
  await branchBaseOps.update(tx, {
    id: branchId,
    inlineSuggestionAppliedAt: now,
  })

  undo.push({ op: 'clearBranchAppliedAt', args: { branchId } })

  return { undo }
}
